import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import { getSelectedCustomerSelector } from '@sophocles/features/auth/authSelectors';
import { getSelectedPlantSelector } from '@sophocles/features/sites/slices/selectedPlant/selectors';
import { getSelectedStructure } from '@sophocles/features/sites/slices/selectedStructure/selectors';
import { siteGroupLandingPath } from '@sophocles/router/paths';

/**
 * Build breadcrumbs from route params and Redux state.
 *
 * Hierarchy: Customer Name > Site Group Name > Site Name (> Reports).
 * Inside /services/ the trail leads with a Services root crumb and the same
 * hierarchy navigates the services flow instead of the customer-tier grids;
 * services pages outside a project (prices, reporting, crews) still get the
 * root crumb, with the customer level as plain text when there is no
 * project to link through. Adds a "Reports" crumb when on a
 * /reports/<subpage> path so users can navigate back to the report listing.
 * Every crumb includes a `type` (the context level, used to pick an icon);
 * the consumer (TitleBar / ContextBreadcrumbs) decides which is "current".
 * A crumb whose target is the page being viewed carries no `to` — a
 * self-link navigates nowhere, so it renders as plain text instead of a
 * clickable dead end.
 *
 * @returns {Array<{label: string, to: (string|undefined), type: string,
 *   isVirtual: (boolean|undefined)}>}
 */
export default function useBreadcrumbs() {
  const params = useParams();
  const location = useLocation();
  const { t } = useTranslation();
  const customer = useSelector(getSelectedCustomerSelector);
  const plant = useSelector(getSelectedPlantSelector);
  const structure = useSelector(getSelectedStructure);

  const customerId = params.cust_id || params.customerId;
  const plantId = params.plant_id || params.sitegroup_id || params.siteGroupId;
  const structureId = params.structure_id || params.site_id || params.siteId;

  const customerName = customer?.get?.('name') || '';
  const plantName = plant?.get?.('name') || '';
  const plantDescription = plant?.get?.('description') || '';
  const isPlantVirtual = Boolean(plant?.get?.('is_virtual'));
  const isGroupInventory = Boolean(plant?.get?.('is_inventory'));
  const structureName = structure?.get?.('name') || '';
  const pathname = location.pathname;

  const projectId = params.projectId;

  return useMemo(() => {
    // Inside the services area the same hierarchy navigates the services
    // flow — a crumb must not teleport into the customer-tier grids. Level
    // crumbs only link when a project id gives them a services target;
    // services pages outside a project (prices) show the level as text.
    const isServicesArea = pathname.startsWith('/services');
    const isServicesFlow = isServicesArea && projectId != null;
    const servicesBase = `/services/customer/${customerId}/project/${projectId}`;

    // Inside the services flow a crumb links to its services-tier target;
    // outside a project it renders as text; outside the services area
    // entirely it links to its customer-tier target.
    const resolveCrumbLink = (servicesSuffix, legacyHref) =>
      isServicesArea ? (isServicesFlow ? `${servicesBase}${servicesSuffix}` : undefined) : legacyHref;

    if (!isServicesArea && (!customerId || !customerName)) return [];

    const crumbs = [];

    if (isServicesArea) {
      crumbs.push({ label: t('appSelection.services', 'Services'), to: '/services/', type: 'services' });
    }

    if (customerId && customerName) {
      crumbs.push({
        label: customerName,
        to: resolveCrumbLink('/', `/c/${customerId}/plants`),
        type: 'customer',
      });
    }

    if (customerId && plantId && plantName) {
      const siteGroupLabel = plantDescription ? `${plantName} / ${plantDescription}` : plantName;
      crumbs.push({
        label: siteGroupLabel,
        to: resolveCrumbLink(
          `/sitegroup/${plantId}/`,
          siteGroupLandingPath({ id: plantId, is_inventory: isGroupInventory }, customerId),
        ),
        type: 'siteGroup',
        // A virtual group holds sites another group owns. The trail is the
        // only chrome that says which group is being looked at, so it says
        // which kind — the same cloud the group grid marks the row with.
        isVirtual: isPlantVirtual,
      });
    }

    if (customerId && structureId && plantId && structureName) {
      crumbs.push({
        label: structureName,
        to: resolveCrumbLink(
          `/sitegroup/${plantId}/site/${structureId}/jobs`,
          `/c/${customerId}/plant/${plantId}/structure/${structureId}`,
        ),
        type: 'site',
      });
    }

    // Add Reports crumb when on a reports sub-page (e.g. /reports/digitalmaintenance).
    // Match either /plant/:id/reports/<sub> or /c/:id/reports/<sub>.
    const reportsMatch = pathname.match(/\/reports\/[^/]+/);
    if (reportsMatch) {
      const reportsListPath = plantId ? `/c/${customerId}/plant/${plantId}/reports` : `/c/${customerId}/reports`;
      crumbs.push({ label: t('general.reports'), to: reportsListPath, type: 'reports' });
    }

    // De-link crumbs that target the page being viewed (e.g. the site-group
    // crumb on that group's own sites list) — the trailing-slash difference
    // between the link and the route is cosmetic, so compare normalized.
    const normalizePath = path => path.replace(/\/+$/, '');
    const currentPath = normalizePath(pathname);
    return crumbs.map(crumb =>
      crumb.to && normalizePath(crumb.to) === currentPath ? { ...crumb, to: undefined } : crumb,
    );
  }, [
    customerId,
    plantId,
    structureId,
    projectId,
    customerName,
    plantName,
    plantDescription,
    isPlantVirtual,
    isGroupInventory,
    structureName,
    pathname,
    t,
  ]);
}
